import { useState, type FormEvent } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { useLabSamples, useSampleByBarcode } from './hooks';
import { LabStatusBadge, PriorityBadge, fmtDateTime, fmtRelative, rejectionLabel } from './ui';
import SampleActions from './SampleActions';

/**
 * Sample management: the collection round, the receiving bench, and the two
 * exception queues.
 *
 * These are one list asking four different questions, which is why they are
 * status filters over one endpoint rather than four screens — the actions
 * available on a row come from the row's own status (see SampleActions), so a
 * tube that moves between queues does not need the screen to know about it.
 */

const QUEUES = [
  { id: 'PENDING_COLLECTION',    label: 'Collection' },
  { id: 'COLLECTED',             label: 'Receiving' },
  { id: 'ACCEPTED,PROCESSING',   label: 'In the laboratory' },
  { id: 'REJECTED',              label: 'Rejected' },
  { id: 'RECOLLECTION_REQUIRED', label: 'Recollection' },
] as const;

export interface SamplesTabProps {
  initialFilter?: Record<string, string>;
}

export default function SamplesTab({ initialFilter }: SamplesTabProps) {
  const [status, setStatus] = useState<string>(initialFilter?.status ?? 'PENDING_COLLECTION');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [scanInput, setScanInput] = useState('');
  const [scanned, setScanned] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useLabSamples({
    status, q: q.trim() || undefined, page,
  });
  const scan = useSampleByBarcode(scanned);

  const rows = data?.data ?? [];

  const submitScan = (e: FormEvent) => {
    e.preventDefault();
    setScanned(scanInput.trim() || null);
  };

  return (
    <div className="space-y-3">
      {/*
        The scan box is first and autofocused: a handheld scanner is a keyboard
        that types a code and presses Enter, so the receiving bench's whole
        interaction is "scan, look, accept" without touching the mouse.
      */}
      <form onSubmit={submitScan} className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3">
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Scan or type a barcode
          </label>
          <Input
            autoFocus
            placeholder="Scan the tube…"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            className="font-mono"
          />
        </div>
        <Button type="submit" size="sm">Find sample</Button>
        {scanned && <Button type="button" variant="ghost" size="sm" onClick={() => { setScanned(null); setScanInput(''); }}>Clear</Button>}
      </form>

      {scanned && scan.isLoading && <LoadingState label="Looking up the barcode…" />}
      {scanned && scan.error && (
        <ErrorState message={(scan.error as Error).message} />
      )}
      {scanned && scan.data && (
        <div className="rounded-xl border-2 border-teal bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-base font-semibold">{scan.data.patient_name}</div>
              <div className="text-[12px] text-muted-foreground">
                {scan.data.uhid} · {scan.data.order_number} · <span className="font-mono">{scan.data.barcode}</span>
              </div>
              <div className="mt-1 text-[12px]">
                {scan.data.sample_type_name} {scan.data.container_name ? `· ${scan.data.container_name}` : ''}
              </div>
              <div className="mt-1 text-[12px] text-muted-foreground">
                Tests: {(scan.data.items ?? []).map((i) => i.test_name).join(', ') || '—'}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                Collected {scan.data.collected_at ? `${fmtDateTime(scan.data.collected_at)} by ${scan.data.collected_by}` : '—'}
              </div>
            </div>
            <LabStatusBadge status={scan.data.status} />
          </div>
          <div className="mt-3 border-t border-border pt-3">
            {/*
              Verify the patient, the order and the sample type against the
              tube in hand BEFORE accepting — that visual check is the reason
              this panel prints all four, and the reason acceptance is a
              deliberate click rather than an automatic consequence of scanning.
            */}
            <SampleActions sample={scan.data} compact />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {QUEUES.map((qq) => (
          <button
            key={qq.id}
            type="button"
            onClick={() => { setStatus(qq.id); setPage(1); }}
            className={`rounded-full px-3 py-1 text-[12px] ${
              status === qq.id ? 'bg-teal text-white font-semibold' : 'border border-border hover:bg-muted'
            }`}
          >
            {qq.label}
          </button>
        ))}
        <Input
          className="ml-auto w-[240px]"
          placeholder="Patient, UHID, order or barcode…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
      </div>

      {isLoading && <LoadingState label="Loading samples…" />}
      {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}
      {!isLoading && !error && rows.length === 0 && (
        <EmptyState label="Nothing in this queue." hint="Samples appear here as orders move through the workflow." />
      )}

      {rows.length > 0 && (
        <>
          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Tests</TableHead>
                  <TableHead>Ordered</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <div className="font-medium">{s.patient_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.uhid} · {s.order_number} <PriorityBadge priority={s.priority} />
                      </div>
                    </TableCell>
                    <TableCell className="text-[12px]">
                      <div>{s.sample_type_name ?? '—'}</div>
                      <div className="text-muted-foreground">{s.container_name ?? ''}</div>
                      {s.barcode && <div className="font-mono text-[11px]">{s.barcode}</div>}
                    </TableCell>
                    <TableCell className="text-[12px]">{(s.tests ?? []).join(', ') || '—'}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      {fmtRelative(s.ordered_at)}
                      {s.rejection_reason && (
                        <div className="text-danger-ink">{rejectionLabel(s.rejection_reason)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <LabStatusBadge status={s.status} />
                      {s.recollection_of_id && <Badge variant="info" className="ml-1">Recollected</Badge>}
                    </TableCell>
                    <TableCell><SampleActions sample={s} compact /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-[12px] text-muted-foreground">
            <span>
              {data?.total ?? rows.length} sample{(data?.total ?? rows.length) === 1 ? '' : 's'}
              {isFetching && ' · refreshing…'}
            </span>
            <span className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page}</span>
              <Button variant="ghost" size="sm" disabled={!data?.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
